import { type FormEvent, useEffect, useRef, useState } from "react";
import { AuthCard, CodeInput } from "@/components/AuthCard";
import { Button } from "@/components/ds/Button";
import { TextInput } from "@/components/ds/Fields";
import { copy } from "@/lib/copy";
import { fetchJson, HttpError, messageDeSecours } from "@/lib/http";

/**
 * L'intervalle entre deux codes, celui que le serveur impose en base
 * (`OTP_COOLDOWN_MS`, `server/src/access/otp.ts`). Recopié et non importé : le
 * front ne tire rien du serveur. Il ne protège rien ici, il évite seulement
 * d'annoncer un code que le serveur n'enverra pas.
 */
const OTP_COOLDOWN_MS = 60 * 1000;

/**
 * La porte à code, en deux temps : l'adresse, puis les six chiffres.
 *
 * Elle vivait dans `MyRequestsPage`. L'espace école a la MÊME porte — même
 * mécanique, mêmes états d'erreur, même absence de compte — et la recopier aurait
 * donné deux portes dont une seule aurait reçu la prochaine correction.
 *
 * Ce qui change d'une porte à l'autre est passé en paramètre : les chemins d'API
 * et les quelques mots du titre. Tout le reste est commun, y compris les trois
 * règles que ces écrans tiennent :
 *
 *  1. la réponse est la MÊME que l'adresse soit connue ou non, sinon le formulaire
 *     dirait qui travaille avec qui ;
 *  2. l'adresse s'affiche en ENTIER, parce qu'elle vient d'être tapée : la masquer
 *     ne la cache à personne et empêche de voir la faute de frappe ;
 *  3. tout appel réseau passe par `fetchJson`, qui pose un délai. Un `fetch` nu
 *     n'en a aucun : une requête que le serveur n'honore jamais laissait le bouton
 *     grisé et le champ muet, sans rien d'autre à faire que recharger la page.
 */

export type OtpGateTexts = {
	eyebrow: string;
	title: string;
	emailLabel: string;
	emailHelp: string;
	emailPlaceholder: string;
	askCode: string;
	enter: string;
};

/**
 * La phrase à écrire sous le champ, à partir de ce que `fetchJson` a levé.
 *
 * `refus` est ce que la porte disait déjà quand le serveur répondait non. Il est
 * plus précis que le message générique, on ne le remplace pas par lui.
 *
 * Le 429 sort du lot partout, y compris à la vérification du code : elle répondait
 * « ce code n'est pas le bon » alors que le serveur n'avait pas regardé le code,
 * il refusait une tentative de plus.
 */
function messagePourEchec(error: unknown, refus: string): string {
	if (error instanceof HttpError) {
		if (error.failure.kind === "http") {
			return error.failure.status === 429 ? copy.tracking.throttled : refus;
		}
		if (error.failure.kind === "network") return copy.tracking.networkFailed;
	}
	// Reste le délai dépassé, qui n'avait aucun texte parce qu'il n'existait pas :
	// sans délai, on n'attendait pas trop longtemps, on attendait pour toujours.
	return messageDeSecours(error);
}

export function OtpGate({
	texts,
	probePath,
	requestPath,
	verifyPath,
	onOpen,
}: {
	texts: OtpGateTexts;
	/**
	 * Route qui répond 200 quand une session est encore ouverte : on ne redemande
	 * pas un code pour trente jours.
	 *
	 * Absente quand l'appelant a DÉJÀ interrogé cette route et sait qu'il n'y a pas
	 * de session — c'est le cas de l'espace école, qui n'affiche cette porte
	 * qu'après un 401. Sonder une seconde fois ne changerait rien à l'écran.
	 */
	probePath?: string;
	requestPath: string;
	verifyPath: string;
	onOpen: () => void;
}) {
	const [step, setStep] = useState<"email" | "code">("email");
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [message, setMessage] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	/**
	 * L'heure du dernier code demandé, et ce que dit l'encadré au-dessus du champ.
	 *
	 * « Attendez une minute » va dans l'encadré, pas sous le champ : ce n'est pas le
	 * code saisi qui est en cause, et l'écrire en erreur passait le champ en rouge.
	 */
	const envoyeA = useRef(0);
	const [annonce, setAnnonce] = useState<"envoye" | "renvoye" | "attendre">("envoye");

	/**
	 * ⚠️ `onOpen` passe par une référence, et ce n'est pas un détail de style.
	 *
	 * L'appelant écrit `onOpen={() => setOuvert(true)}` : une fonction NEUVE à
	 * chaque rendu. Mise dans les dépendances de l'effet, elle relançait la sonde à
	 * chaque frappe dans le champ email — une requête par caractère.
	 */
	const ouvrir = useRef(onOpen);
	ouvrir.current = onOpen;

	useEffect(() => {
		if (!probePath) return;
		const abandon = new AbortController();
		fetchJson(probePath, { signal: abandon.signal })
			.then(() => {
				// L'abandon coupe la requête, pas une réponse déjà arrivée : sans ce
				// test, une sonde revenue pendant le démontage ouvrirait une porte qui
				// n'est plus à l'écran.
				if (!abandon.signal.aborted) ouvrir.current();
			})
			.catch(() => {
				// Pas de session à retrouver, réseau coupé, ou délai dépassé : la porte
				// s'affiche, et c'est le bon comportement. Un message ici parlerait d'un
				// problème que la personne n'a pas.
			});
		return () => abandon.abort();
	}, [probePath]);

	async function askCode(event: FormEvent) {
		event.preventDefault();
		setBusy(true);
		setMessage(null);
		try {
			await fetchJson(requestPath, { method: "POST", body: { email } });
			envoyeA.current = Date.now();
			setAnnonce("envoye");
			setStep("code");
		} catch (error) {
			setMessage(messagePourEchec(error, copy.tracking.throttled));
		} finally {
			setBusy(false);
		}
	}

	/**
	 * Le geste que « Rien reçu ? » annonçait sans le proposer.
	 *
	 * Pendant la première minute, le serveur refuse un second code sans le dire (sa
	 * réponse est la même dans tous les cas). On ne l'appelle donc pas, et on dit
	 * d'attendre : annoncer un nouveau code qui ne partira pas ferait guetter un mail
	 * qui n'existe pas.
	 */
	async function resend() {
		setMessage(null);
		if (Date.now() - envoyeA.current < OTP_COOLDOWN_MS) {
			setAnnonce("attendre");
			return;
		}
		setBusy(true);
		try {
			await fetchJson(requestPath, { method: "POST", body: { email } });
			envoyeA.current = Date.now();
			setAnnonce("renvoye");
		} catch (error) {
			setMessage(messagePourEchec(error, copy.tracking.throttled));
		} finally {
			setBusy(false);
		}
	}

	async function verify(event: FormEvent) {
		event.preventDefault();
		// Le bouton reste actif : grisé tant que les six chiffres n'y sont pas, il ne
		// disait pas ce qui manquait. Il le dit au clic, sous le champ.
		if (code.length !== 6) {
			setMessage(copy.tracking.codeIncomplete);
			return;
		}
		setBusy(true);
		setMessage(null);
		try {
			await fetchJson(verifyPath, { method: "POST", body: { email, code } });
			onOpen();
		} catch (error) {
			setMessage(messagePourEchec(error, copy.tracking.codeRejected));
		} finally {
			setBusy(false);
		}
	}

	if (step === "code") {
		return (
			<AuthCard
				eyebrow={texts.eyebrow}
				title={texts.title}
				footer={
					<div className="flex flex-col gap-3">
						<p className="t-body text-mg-secondary">{copy.tracking.wrongAddress}</p>
						<div className="flex flex-wrap gap-2">
							<Button size="sm" disabled={busy} onClick={resend}>
								{copy.tracking.resend}
							</Button>
							<Button
								size="sm"
								onClick={() => {
									setStep("email");
									setCode("");
									setMessage(null);
								}}
							>
								{copy.tracking.changeEmail}
							</Button>
						</div>
					</div>
				}
			>
				<p
					role="status"
					className="mb-6 rounded-field bg-mg-subtle px-4 py-3 t-body text-mg-heading"
				>
					{annonce === "attendre"
						? copy.tracking.resendTooSoon
						: annonce === "renvoye"
							? copy.tracking.resent(email)
							: copy.tracking.codeSent(email)}
				</p>
				<form onSubmit={verify} noValidate className="flex flex-col gap-5">
					<CodeInput
						value={code}
						onChange={setCode}
						label={copy.tracking.codeLabel}
						error={message}
					/>
					<Button tone="primary" full type="submit" disabled={busy}>
						{texts.enter}
					</Button>
				</form>
			</AuthCard>
		);
	}

	return (
		<AuthCard eyebrow={texts.eyebrow} title={texts.title}>
			<form onSubmit={askCode} className="flex flex-col gap-5">
				<TextInput
					id="email"
					type="email"
					required
					autoComplete="email"
					label={texts.emailLabel}
					hint={texts.emailHelp}
					placeholder={texts.emailPlaceholder}
					error={message}
					value={email}
					onChange={setEmail}
				/>
				<Button tone="primary" full type="submit" disabled={busy}>
					{texts.askCode}
				</Button>
			</form>
		</AuthCard>
	);
}
